/**
 * Turns procedure-block availability into the day chips the warm hand-off card
 * offers.
 *
 * The `find-availability` bot returns one window per contiguous free span at the
 * queried location, so a single procedure day arrives as several windows. The card
 * offers *days*, not times: the chip records the date the patient agreed to and
 * books nothing, because the CC team schedules once payment completes.
 */

import { FindAvailabilityWindow } from '@bariendo/lib/scheduling';
import dayjs from 'dayjs';

/**
 * Three days offered, per the card design — plus the "not ready to commit yet"
 * chip the grid adds itself.
 *
 * The spoken line names only the first two; reading three dates aloud is
 * unwieldy, and its closing clause ("or is there another timeframe you had in
 * mind?") covers the rest.
 */
export const PROCEDURE_DATE_LIMIT = 3;

export interface ProcedureDateOption {
  /** The day, as an ISO date — what gets recorded on the note. */
  date: string;
  /** How a rep reads it aloud, e.g. "Tue, Sep 1". */
  label: string;
  /**
   * The physician whose block it is. Absent when the bot returned an unscoped
   * window; the chip still stands, since the date is what the patient agrees to.
   */
  physicianReference?: string;
  /**
   * Resolved separately from the availability read — windows carry a reference,
   * not a name. Absent when the lookup failed, which degrades the chip label
   * rather than dropping the date.
   */
  physicianName?: string;
}

export function toProcedureDateOptions(
  windows: FindAvailabilityWindow[],
  limit: number = PROCEDURE_DATE_LIMIT
): ProcedureDateOption[] {
  const byDay = new Map<string, { start: dayjs.Dayjs; physicianReference?: string }>();

  for (const window of windows) {
    const start = dayjs(window.startTime);
    // A malformed window is dropped rather than allowed to render an "Invalid
    // Date" chip a rep might read out.
    if (!start.isValid()) continue;

    const day = start.format('YYYY-MM-DD');
    const existing = byDay.get(day);

    // Earliest block of the day wins the physician: two physicians can be blocked
    // at one site on one day, and the chip names a single one.
    if (!existing || start.isBefore(existing.start)) {
      byDay.set(day, { start, ...(window.resourceId ? { physicianReference: window.resourceId } : {}) });
    }
  }

  return (
    [...byDay.entries()]
      // Sorted here rather than assumed of the bot: it merges weekly and one-time
      // availability, and nothing in its contract promises an order.
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, limit)
      .map(([date, { start, physicianReference }]) => ({
        date,
        label: start.format('ddd, MMM D'),
        ...(physicianReference ? { physicianReference } : {}),
      }))
  );
}
